import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../stores/types/types';
import { contrastingColor, getNameOrInitials } from '../../utils/utils';
import type { Invitation } from '../../stores/types/invitations';
import type { ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import useShots from '../../contexts/shots/provider';
import type { DragItem } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import VideoFeedContextMenu from './video-feed-context-menu';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import useProfile from '../../hooks/use-profile';
import { ProfileInterface } from '../../stores/types/user-profiles';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import Avatar, { AvatarUser } from '../general-components/avatar';
import colors from '../../utils/colors';
import { ICONS, COLORS } from '../general-components/icon';
import FeedPhoneDialer from './feed-phone-dialer';
import Iconv2 from '../general-components/icon-v2/icon';
import { VideoStream } from '../types/videos';

interface Props {
	invite: Invitation;
	isForShotModal?: boolean;
	dropCallback?: (item: DragItem, dropData: any) => void;
}

export default function VideoFeedFakeThumbnail(props: Props) {
	const { 
		invite, 
		isForShotModal = false, 
		dropCallback = () => {}
	} = props;

	const dispatch = useDispatch();

	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();
	const [dialerOpen, setDialerOpen] = useState(false);
	const [callPending, setCallPending] = useState(false);
	const [sipConnectionId, setSipConnectionId] = useState<string | null>(null);

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteUsers = useSelector((s: GS) => s.SessionReducer.userMeetingSettings.onsiteUsers);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const usePstn = useSelector((s: GS) => s.SettingsReducer.featureFlags.use_pstn);

	const { currentShot } = useShots();
	const {watchedProfile} = useProfile({suggestedProfile: invite.profile.gr_user});

	const shotFeeds = useMemo(
		() => (isForShotModal ? shotModalShot.shot_data.shotFeeds : currentShot.shot_data.shotFeeds),
		[currentShot, shotModalShot, isForShotModal],
	);

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `video-feed-${invite.profile.gr_user}`, [invite.profile.gr_user]);

	const isOnsiteUser = useMemo(
		() => (!invite.profile.gr_user ? false : !!onsiteUsers?.includes(invite.profile.gr_user)),
		[onsiteUsers, invite],
	);

	function getPosterImageOld(invite2: Invitation) {
		if (!invite2.profile) return '';
		if (invite2.profile.poster || invite2.profile.avatar) {
			return invite2.profile.poster || invite2.profile.avatar;
		}
		return '';
	}

	function getPosterImage(invite2: ProfileInterface) {
		if (!invite2) return '';
		if (invite2.bg_image || invite2.avatar) {
			return invite2.bg_image || invite2.avatar;
		}
		return '';
	}

	const hasActivePhoneCall = useMemo(
		() => invite.profile.gr_user
			? connectedPresenterVideos.some(
				(v: VideoStream) => v.isPhoneCall && v.profile === invite.profile.gr_user,
			)
			: false,
		[connectedPresenterVideos, invite.profile.gr_user],
	);

	useEffect(() => {
		if (hasActivePhoneCall) setCallPending(false);
	}, [hasActivePhoneCall]);

	const callIsActive = hasActivePhoneCall || callPending;
	const meetingId = meeting?.meeting_id;

	const clearCallState = () => {
		setCallPending(false);
		setSipConnectionId(null);
		setDialerOpen(false);
	};

	const inShot = useMemo(() => shotFeeds.some((feed: ShotFeed) => {
		if (feed.feedType === FEED_TYPES.EMAIL) {
			return feed.email === invite.profile.email;
		} if (feed.feedType === FEED_TYPES.PRESENTER) {
			return feed.presenter === invite.profile.gr_user;
		}

		return null;
	}), [invite, shotFeeds]);

	const dndProps = useMemo<DragItem>(() => {
		if (invite.profile.gr_user) {
			return {
				id: invite.profile.gr_user,
				type: DRAG_TYPE.FEED_PRESENTER,
			};
		}
		return {
			id: invite.profile.email,
			type: DRAG_TYPE.FEED_EMAIL,
		};
	}, [invite]);

	const { dragRef, isDragging } = useDragItem(dndProps, host);

	const imageSrc = watchedProfile ? getPosterImage(watchedProfile) : getPosterImageOld(invite);

	const { dropRef } = useDropArea({
		accepts: DRAG_TYPE.PROFILE_MASK,
		dropCallback: dropCallback,
		dropAreaData: {
			id: watchedProfile?.email ?? 'none',
		},
	});

	return (
		<>
			{!!invite.profile.gr_user && (
				<VideoFeedContextMenu
					clickCoordinates={rightClickCoords}
					contextMenuId={contextMenuId}
					socket={null}
					userId={invite.profile.gr_user}
				/>
			)}
					<React.Fragment>
						{isDragging && (<div className="video-thumb-drag-placeholder" />)}
						<div
							ref={dragRef}
							onContextMenu={(e) => {
								if (host) {
									e.preventDefault();
									setRightClickCoords([e.pageX, e.pageY]);
									dispatch(setOpenContextMenuId(contextMenuId));
								}
							}}
							onFocus={(e) => e.currentTarget.blur()}
							id={String(invite.code)}
							className={classNames('video-thumb', {
								'is-dragging': isDragging,
								'cursor-grab': host,
							})}
						>
							<div className="video-wrapper" ref={dropRef}>
								<div className="video-overlay">
									<div className={classNames('poster')}>
										{imageSrc
											? <img className="poster-image" src={imageSrc} alt={''} />
											: <div className="no-image-placeholder">
												<span style={{
													backgroundColor: meeting?.primary_color ? `#${meeting.primary_color}` : '#333',
													color: contrastingColor(meeting?.primary_color ?? colors.gray33),
												}}>
													{getNameOrInitials(watchedProfile ?? invite.profile, false)}
												</span>
											</div>
										}
									</div>
									{isOnsiteUser && (
										<div className="producer-label" data-testid="video-feed-fake-thumbnail-label">
										<span>ONSITE</span>
										</div>
									)}
								</div>

								<div className={classNames('video-overlay')}>
									<div className="video-overlay-bottom">
										<label className='font-size-14 font-weight-heavy'>{getNameOrInitials(watchedProfile ?? invite.profile, true)}</label>
										{host && usePstn && (
											<button
												className={classNames('feed-phone-btn', { active: callIsActive })}
												onClick={(e) => {
													e.stopPropagation();
													setDialerOpen((o) => !o);
												}}
												title={callIsActive ? (dialerOpen ? 'Hide dialer' : 'Show dialer') : 'Dial phone'}
											>
												<Iconv2 name={ICONS.PHONE} size={12} color={callIsActive ? COLORS.GREEN : COLORS.WHITE} />
											</button>
										)}
									</div>
								</div>

								{dialerOpen && usePstn && (
									<FeedPhoneDialer
										identity={{
											gr_user: invite.profile.gr_user,
											email: invite.profile.email,
											first_name: watchedProfile?.first_name ?? invite.profile.first_name,
											last_name: watchedProfile?.last_name ?? invite.profile.last_name,
											poster: invite.profile.poster,
											phone: watchedProfile?.phone ?? invite.profile.phone,
										}}
										onClose={() => setDialerOpen(false)}
										onCallStarted={(id) => { setCallPending(true); setSipConnectionId(id); }}
										onHangup={clearCallState}
										isCallConnected={hasActivePhoneCall}
										activeSipConnectionId={sipConnectionId}
									/>
								)}

								<div className={classNames('selection-border', {
									'in-shot': inShot,
								})} />
							</div>
						</div>
					</React.Fragment>
					</>
	);
}
